import {
  DragSourceMonitor,
  DropTargetMonitor,
  useDrag,
  useDrop,
} from 'react-dnd';
import { NavLink } from 'react-router-dom';
import { Text, Link, IconButton, Flex } from '@chakra-ui/react';
import { useAppDispatch } from 'app/store';
import {
  reorderPinnedSongsEffect,
  unpinSongEffect,
} from 'app/store/SongList/effects';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { useState } from 'react';
import { SongCover } from '../Song/SongCover';
import { PinnedSong } from './types';
import { getPinnedSongItemSx, iconBtnSx, linkSx, textSx } from './styles';
import { ContextMenuProps } from '../ChakraTableList/ContextMenu/types';
import { isOverLeft } from './utils';
import { PIN_DRAG_EVENT_TYPE } from './constants';

interface DragAndDropItem {
  guid: string;
}

export const PinnedSongItem = ({
  guid,
  href,
  label,
  menuOptions,
  status,
  subLabel,
  contextMenuProps,
}: PinnedSong & { contextMenuProps: ContextMenuProps }) => {
  const { setOptions, setPosition } = contextMenuProps;
  const dispatch = useAppDispatch();
  const [isLeft, setIsLeft] = useState(false);
  let divRef: HTMLDivElement;

  const [{ isDragging }, drag] = useDrag({
    type: PIN_DRAG_EVENT_TYPE,
    item: { guid, label },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: PIN_DRAG_EVENT_TYPE,
    drop({ guid: songGuid }: DragAndDropItem) {
      if (guid !== songGuid) {
        dispatch(
          reorderPinnedSongsEffect({
            songGuid,
            targetSongGuid: guid,
            before: isLeft,
          })
        );
      }
    },
    hover: (_: DragAndDropItem, monitor: DropTargetMonitor) => {
      setIsLeft(isOverLeft(divRef, monitor));
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleContextMenu = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    options
  ) => {
    setOptions(options);
    const x = e.clientX;
    const y = e.clientY;
    setPosition({ x, y });
    e.preventDefault();
  };

  const getCursor = (defaultCursor?: 'pointer') =>
    isOver || isDragging ? 'grabbing' : defaultCursor;

  return (
    <Flex
      ref={(ref) => {
        if (ref) {
          divRef = ref;
        }
        drag(drop(ref));
      }}
      data-testid="pinned-song"
      sx={getPinnedSongItemSx(isOver, isLeft)}
      cursor={getCursor()}
    >
      <Link
        as={NavLink}
        to={href}
        onContextMenu={(e) => handleContextMenu(e, menuOptions)}
        sx={linkSx}
        pointerEvents={isDragging ? 'none' : undefined}
        cursor={getCursor('pointer')}
        onMouseDown={(e) => e.preventDefault()}
      >
        <IconButton
          aria-label="unpin"
          variant="text"
          size="xs"
          icon={<CloseIcon width={16} height={16} />}
          data-testid="unpin-button"
          onClick={(e) => {
            e.preventDefault();
            dispatch(unpinSongEffect({ guid, title: label }));
          }}
          sx={iconBtnSx}
        />
        <SongCover
          status={status}
          sx={{
            mb: 2,
          }}
          size="big"
        />
        <Text sx={textSx} title={label}>
          {label}
        </Text>

        <Text
          sx={{
            ...textSx,
            fontSize: 'xs',
            color: 'textSubdued',
          }}
          title={subLabel}
        >
          {subLabel}
        </Text>
      </Link>
    </Flex>
  );
};
